<template>
  <div
    ref="container-element"
    class="handover-document-scan-container relative grid h-full grid-rows-[auto_auto_auto_1fr_auto] flex-col gap-4"
  >
    <div class="absolute right-0 top-0 flex items-center gap-4">
      <CVButton
        v-if="hasTorch"
        type="button"
        variant="outline"
        class="inline-flex rounded-full p-2 hover:bg-gray-100"
        :class="{ '!bg-obsidian-600': isTorchOn }"
        @click="handleToggleTorch"
      >
        <ZapIcon class="h-4 w-4" :class="{ 'text-yellow-200': isTorchOn }" />
      </CVButton>
      <button type="button" data-testid="close-document-scan" @click="$emit('close')">
        <XIcon class="h-4 w-4" />
      </button>
    </div>
    <div class="handover-document-scan-title">
      <h4 class="text-lg font-medium">{{ t('scanDocuments') }}</h4>
      {{ driverSubject > 0 ? t('forAdditionalDriver', { n: driverSubject }) : t('forMainDriver') }}
    </div>

    <div class="handover-document-scan-requirements grid grid-cols-2 gap-2 text-xs">
      <div
        class="font-bold"
        :class="{
          'text-green-700': requireId,
          'text-obsidian-400 line-through': !requireId,
        }"
      >
        <Information :class="{ 'pointer-events-none': !requireId }">
          <template #text>
            <IdCardIcon class="inline-icon" />
            {{ t('idCard') }}
          </template>
          <template #modal>
            <p class="text-sm">
              {{ t('idInfo') }}
            </p>
          </template>
        </Information>
      </div>
      <div
        class="font-bold"
        :class="{
          'text-green-700': requireLicense,
          'text-obsidian-400 line-through': !requireLicense,
        }"
      >
        <CarIcon class="inline-icon" />
        {{ t('driverLicense') }}
      </div>
      <span class="handover-document-scan-driver-info">
        {{
          driverSubject > 0 ? t('forAdditionalDriver', { n: driverSubject }) : t('forMainDriver')
        }}
      </span>
    </div>
    <div
      ref="video-feed"
      class="handover-document-scan-video relative aspect-[1.586] max-h-[40vh] max-w-full overflow-hidden"
    >
      <video
        ref="videoRef"
        class="absolute inset-0 h-full w-full rounded-lg object-cover"
        autoplay
        playsinline
        muted
      />
      <div
        class="absolute inset-0 bg-white transition-none duration-200"
        :class="{ 'opacity-0 !transition-opacity': !isFlashing }"
      ></div>
    </div>

    <div class="handover-document-scan-images relative min-h-0 flex-1">
      <div
        class="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-white to-transparent transition-opacity duration-200"
        :class="{ 'opacity-0': arrivedState.top }"
      ></div>
      <div
        class="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 bg-gradient-to-t from-white to-transparent transition-opacity duration-200"
        :class="{ 'opacity-0': arrivedState.bottom }"
      ></div>
      <div
        ref="scrollContainer"
        class="handover-document-scan-scroll-container grid h-full grid-rows-[auto] content-start gap-4 overflow-y-auto max-sm:px-6 sm:grid-cols-2 md:grid-cols-3"
      >
        <div v-for="(image, index) in images" :key="index" class="relative">
          <img
            :src="image.url"
            class="w-full rounded-lg object-cover"
            :style="{ aspectRatio: 1.586 }"
          />
          <button
            type="button"
            class="absolute right-2 top-2 rounded-full bg-red-500 p-2 text-white hover:bg-red-600"
            @click="removeImage(index)"
          >
            <XIcon class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Action buttons -->
    <div class="handover-document-scan-actions flex justify-between gap-4">
      <CVButton variant="secondary" type="button" @click="captureImage">
        <CameraIcon class="h-4 w-4" />
        {{ t('takePhoto') }}
      </CVButton>
      <CVButton
        variant="default"
        type="button"
        :disabled="images.length < (requireId ? 1 : 0) + (requireLicense ? 1 : 0)"
        @click="extractData"
      >
        {{ t('finish') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, onUnmounted, ref, useTemplateRef } from 'vue';
import { useCameraStream } from '@/hooks/use-camera-stream';
import { useI18n } from 'vue-i18n';
import CVButton from '@/components/buttons/CVButton.vue';
import { useMediaQuery, useResizeObserver, useScroll } from '@vueuse/core';
import { CameraIcon, CarIcon, IdCardIcon, XIcon, ZapIcon } from 'lucide-vue-next';
import Information from '@/components/Information.vue';

// Extend MediaTrackCapabilities and MediaTrackConstraintSet to include torch
declare global {
  interface MediaTrackCapabilities {
    torch?: boolean;
  }
  interface MediaTrackConstraintSet {
    torch?: boolean;
  }
}

const containerElement = useTemplateRef('container-element');
const videoFeed = useTemplateRef('video-feed');

const isMobileLandscape = useMediaQuery('(orientation: landscape) and (max-height: 800px)');

function updateFeedPosition() {
  if (!containerElement.value || !videoFeed.value) return;
  const { width, height } = videoFeed.value.getBoundingClientRect();
  const { width: containerWidth, height: containerHeight } =
    containerElement.value.getBoundingClientRect();
  if (isMobileLandscape.value) {
    videoFeed.value.style.translate = `0px ${containerHeight / 2 - height / 2}px`;
  } else {
    videoFeed.value.style.translate = `${containerWidth / 2 - width / 2}px 0px`;
  }
}

useResizeObserver(containerElement, updateFeedPosition);
useResizeObserver(videoFeed, updateFeedPosition);

defineProps<{
  /** 0 for the main driver, n > 0 for any additional driver n */
  driverSubject: number;
  requireId: boolean;
  requireLicense: boolean;
}>();

const emit = defineEmits<{
  (e: 'scanned', data: File[]): void;
  (e: 'close'): void;
}>();

const { t } = useI18n();

const videoRef = ref<HTMLVideoElement | null>(null);
const images = ref<{ file: File; url: string }[]>([]);
const scrollContainer = ref<HTMLElement | null>(null);
const isFlashing = ref(false);
const {
  stream,
  hasTorch,
  isTorchOn,
  toggleTorch: handleToggleTorch,
} = useCameraStream(
  videoRef,
  // Prefer the back camera, framed like an ID card (85.6mm / 54mm).
  { video: { facingMode: 'environment', aspectRatio: 1.586, width: 2048 } },
  () => console.error('Failed to initialize camera'),
);

const { arrivedState } = useScroll(scrollContainer, {
  offset: { top: 10, bottom: 10 },
});

// Capture image from video feed
const captureImage = () => {
  if (!videoRef.value || !stream.value) return;

  // Trigger flash animation
  isFlashing.value = true;
  setTimeout(() => {
    isFlashing.value = false;
  }, 50);

  const video = videoRef.value;
  const canvas = document.createElement('canvas');
  const aspectRatio = 1.586; // ID card aspect ratio (85.6mm / 54mm)

  // Use 2048px width for better quality while keeping file size reasonable
  const targetWidth = 1400;
  canvas.width = targetWidth;
  canvas.height = targetWidth / aspectRatio;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Enable image smoothing for better quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Calculate the crop region to match the overlay
  const sourceWidth = video.videoWidth; // 80% of width as shown in overlay
  const sourceHeight = sourceWidth / aspectRatio;
  const sourceX = (video.videoWidth - sourceWidth) / 2;
  const sourceY = (video.videoHeight - sourceHeight) / 2;

  // Draw and crop the video frame at high resolution
  ctx.drawImage(
    video,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  // Convert canvas to file with good quality
  canvas.toBlob(
    (blob) => {
      if (!blob) return;

      // Create file from blob
      const file = new File([blob], 'file.jpg', {
        type: 'image/jpeg',
      });

      const url = URL.createObjectURL(blob);
      images.value.push({ file, url });
      // Scroll to bottom after adding new image
      nextTick(() => {
        if (scrollContainer.value) {
          scrollContainer.value.scrollTo({
            top: scrollContainer.value.scrollHeight,
            behavior: 'smooth',
          });
        }
      });
    },
    'image/jpeg',
    0.8,
  );
};

const removeImage = (index: number) => {
  URL.revokeObjectURL(images.value[index].url);
  images.value.splice(index, 1);
};

const extractData = async () => {
  try {
    emit(
      'scanned',
      images.value.map((image) => image.file),
    );
  } catch (err) {
    console.error('Failed to extract data:', err);
  }
};

onUnmounted(() => {
  images.value.forEach((image) => URL.revokeObjectURL(image.url));
});
</script>

<i18n lang="json">
{
  "en": {
    "scanDocuments": "Scan documents",
    "capturedImages": "Captured Images",
    "clear": "Clear All",
    "finish": "Finish",
    "takePhoto": "Scan",
    "extracting": "Extracting data...",
    "forMainDriver": "For Main Driver",
    "forAdditionalDriver": "for additional driver {n}",
    "idCard": "ID / Passport",
    "driverLicense": "Driver License",
    "idInfo": "For IDs, please scan front and back. For passports, only scan the relevant information."
  },
  "de": {
    "scanDocuments": "Dokumente scannen",
    "capturedImages": "Aufgenommene Bilder",
    "clear": "Alle löschen",
    "finish": "Fertig",
    "takePhoto": "Scannen",
    "extracting": "Daten werden extrahiert...",
    "forAdditionalDriver": "für Zusatzfahrer {n}",
    "forMainDriver": "für Hauptfahrer",
    "idCard": "Ausweis / Pass",
    "driverLicense": "Führerschein",
    "idInfo": "Für Ausweise, bitte vorne und hinten scannen. Für Pässe, nur die relevanten Informationen scannen."
  },
  "es": {
    "scanDocuments": "Escanear documentos",
    "capturedImages": "Imágenes capturadas",
    "clear": "Borrar todo",
    "finish": "Finalizar",
    "takePhoto": "Escanear",
    "extracting": "Extrayendo datos…",
    "forMainDriver": "Conductor principal",
    "forAdditionalDriver": "conductor adicional {n}",
    "idCard": "DNI / pasaporte",
    "driverLicense": "Permiso de conducir",
    "idInfo": "Para documentos de identidad, escanee anverso y reverso. Para pasaportes, solo la información relevante."
  }
}
</i18n>

<style scoped>
@media (orientation: landscape) and (max-height: 800px) {
  .handover-document-scan-container {
    grid-template-columns: 2fr 1fr auto;
    grid-template-rows: 1fr auto;
  }

  .handover-document-scan-title {
    display: none;
  }

  .handover-document-scan-requirements {
    grid-column-start: 3;
    grid-template-columns: 1fr;
    align-self: end;
  }

  .handover-document-scan-driver-info {
    display: block;
  }

  .handover-document-scan-video {
    grid-row: span 2 / span 2;
    grid-row-start: 1;
    max-height: 100%;
    max-width: 50vw;
  }

  .handover-document-scan-images {
    grid-row: span 2 / span2;
    grid-row-start: 1;
  }

  .handover-document-scan-scroll-container {
    grid-template-columns: 1fr !important;
  }

  .handover-document-scan-actions {
    display: grid;
    justify-content: stretch;
    align-self: end;
  }
}
</style>
